import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { bonusApi } from "../services/bonus/api";
import {
  //BonusApplication,
  BonusQueryParams,
  EditBonusApplicationPayload,
  BonusApplication,
  Project,
  FileAttachment,
  BonusDetailsResponse,
} from "../services/bonus/types";
import { RoleType } from "../components/CreateButton/constants";
import { StatusFilterState } from "../pages/admin/dashboard/types";
import httpClient, { TimeoutError } from "../utils/httpClient";

// Create a type for the bonusKeys object first
type BonusQueryKeys = {
  all: readonly ["bonuses"];
  templates: (roleType: string) => readonly ["bonuses", "templates", string];
  applications: (
    params: BonusQueryParams
  ) => readonly ["bonuses", "applications", BonusQueryParams];
  actionItems: (
    params: BonusQueryParams
  ) => readonly ["bonuses", "actionItems", BonusQueryParams];
  userReviews: (
    params: BonusQueryParams
  ) => readonly ["bonuses", "userReviews", BonusQueryParams];
  adminApplications: (
    params: StatusFilterState
  ) => readonly ["bonuses", "adminApplications", StatusFilterState];
  details: (id: number) => readonly ["bonuses", "details", number];
  fileAttachments: {
    all: readonly ["bonuses", "fileAttachments"];
    byEmail: (
      email: string
    ) => readonly ["bonuses", "fileAttachments", "email", string];
    byEmployee: (
      employeeId: number
    ) => readonly ["bonuses", "fileAttachments", "employee", number];
    byQuarter: (
      quarter: string
    ) => readonly ["bonuses", "fileAttachments", "quarter", string];
  };
};

// Define the base key to avoid circular references
const baseKey = ["bonuses"] as const;

// Then define the bonusKeys object
export const bonusKeys: BonusQueryKeys = {
  all: baseKey,
  templates: (roleType: string) => [...baseKey, "templates", roleType] as const,
  applications: (params: BonusQueryParams) =>
    [...baseKey, "applications", params] as const,
  actionItems: (params: BonusQueryParams) =>
    [...baseKey, "actionItems", params] as const,
  userReviews: (params: BonusQueryParams) =>
    [...baseKey, "userReviews", params] as const,
  adminApplications: (params: StatusFilterState) =>
    [...baseKey, "adminApplications", params] as const,
  details: (id: number) => [...baseKey, "details", id] as const,
  fileAttachments: {
    all: [...baseKey, "fileAttachments"] as const,
    byEmail: (email: string) =>
      [...baseKey, "fileAttachments", "email", email] as const,
    byEmployee: (employeeId: number) =>
      [...baseKey, "fileAttachments", "employee", employeeId] as const,
    byQuarter: (quarter: string) =>
      [...baseKey, "fileAttachments", "quarter", quarter] as const,
  },
};

// Template hooks
export const useTemplates = (roleType: string) => {
  const validRoleTypes = [
    RoleType.CCM1,
    RoleType.CCM2,
    RoleType.CCM3,
    RoleType.DCCM,
    RoleType.CM3,
    RoleType.PM,
    RoleType.SPM,
  ];

  const isValidRoleType = validRoleTypes.includes(roleType as RoleType);
  return useQuery({
    queryKey: bonusKeys.templates(roleType),
    queryFn: () => bonusApi.getTemplates(roleType),
    enabled: isValidRoleType,
  });
};

// JSON Templates hook
export const useJsonTemplates = (roleType: string) => {
  const validRoleTypes = [
    RoleType.CCM1,
    RoleType.CCM2,
    RoleType.CCM3,
    RoleType.DCCM,
    RoleType.CM3,
    RoleType.PM,
    RoleType.SPM,
  ];

  const isValidRoleType = validRoleTypes.includes(roleType as RoleType);
  return useQuery({
    queryKey: [...bonusKeys.all, "jsonTemplates", roleType],
    queryFn: () => bonusApi.getJsonTemplates(roleType),
    enabled: isValidRoleType,
  });
};

// Projects hook
export const useProjects = () => {
  return useQuery<Project[]>({
    queryKey: [...bonusKeys.all, "projects"],
    queryFn: () => bonusApi.getProjects(),
  });
};

// Hook for fetching bonus applications
export const useBonusApplications = (params: BonusQueryParams) => {
  return useQuery({
    queryKey: ['bonusApplications', params.year, params.quarter],
    queryFn: () => bonusApi.getBonusApplications(params),
    enabled: !!params.year && !!params.quarter,
  });
};

// Hook for fetching bonus application details
export const useBonusDetails = (id: number) => {
  return useQuery<BonusDetailsResponse>({
    queryKey: bonusKeys.details(id),
    queryFn: () => bonusApi.getBonusApplicationDetails(id),
    enabled: id > 0,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
  });
};

// Hook for fetching action items
export const useActionItems = (params: BonusQueryParams) => {
  return useQuery({
    queryKey: ["actionItems", params.year, params.quarter],
    queryFn: () => bonusApi.getMyActionItems(params),
    enabled: !!params.year && !!params.quarter,
  });
};

// Hook for fetching user reviews
export const useUserReviews = (filters: BonusQueryParams) => {
  return useQuery({
    queryKey: ['userReviews', filters.year, filters.quarter],
    queryFn: () => bonusApi.getUserReviews(filters),
    enabled: !!filters.year && !!filters.quarter,
  });
};

export const useMySubmissions = (filters: BonusQueryParams) => {
  return useQuery({
    queryKey: ["mySubmissions", filters.year, filters.quarter],
    queryFn: () => bonusApi.getMySubmissions(filters),
    enabled: !!filters.year && !!filters.quarter,
  });
};


// Hook for fetching admin applications
export const useAdminApplications = (params: StatusFilterState) => {
  return useQuery({
    queryKey: bonusKeys.adminApplications(params),
    queryFn: async () => {
      try {
        const response = await httpClient.get("/api/manage/applications", {
          params: {
            year: params.year,
            quarter: params.quarter,
            applicationType: params.applicationType,
            status: params.status,
            BonusRecipient: params.bonusRecipient,
            region: params.region,
            resultType: params.resultType,
            page: params.page,
            pageSize: params.pageSize,
          },
        });

        if (response.data?.error) {
          throw new Error(response.data.error);
        }

        return {
          data: response.data?.data || [],
          totalCount: response.data?.totalCount || 0,
          page: response.data?.page || 1,
          pageSize: response.data?.pageSize || 25,
        };
      } catch (e) {
        if (e instanceof TimeoutError) {
          console.error(`Request timed out for ${e.config.method.toUpperCase()} ${e.config.url} — ${e.message}. Please check if your changes were saved before trying again`);
        }
        throw e;
      }
    },
    enabled: !!params.year && !!params.quarter && params.resultType === 'json',
  });
};

// Create bonus application hook
export const useCreateBonusApplication = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BonusApplication) =>
      bonusApi.createBonusApplication(data),
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: bonusKeys.all });
      queryClient.invalidateQueries({ queryKey: ['bonusApplications'] });
      queryClient.invalidateQueries({ queryKey: ['actionItems'] });
      queryClient.invalidateQueries({ queryKey: ['userReviews'] });
    },
  });
};

// Hook for editing a bonus application
export const useEditBonusApplication = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: EditBonusApplicationPayload) =>
      bonusApi.editBonusApplication(data),
    onSuccess: (_, variables) => {
      // Invalidate specific application details
      queryClient.invalidateQueries({
        queryKey: bonusKeys.details(variables.BonusApplicationId),
      });
      // Invalidate all bonus-related queries
      queryClient.invalidateQueries({ queryKey: bonusKeys.all });
      queryClient.invalidateQueries({ queryKey: ['bonusApplications'] });
      queryClient.invalidateQueries({ queryKey: ['actionItems'] });
      queryClient.invalidateQueries({ queryKey: ['userReviews'] });
    },
  });
};

// ==================== File Attachment Hooks ====================

// Hook for fetching file attachments by email
export const useFileAttachmentsByEmail = (email: string) => {
  return useQuery<FileAttachment[]>({
    queryKey: bonusKeys.fileAttachments.byEmail(email),
    queryFn: () => bonusApi.getFileAttachmentsByEmail(email),
    enabled: !!email,
  });
};

// Hook for fetching file attachments by employee ID
export const useFileAttachmentsByEmployee = (employeeId: number) => {
  return useQuery<FileAttachment[]>({
    queryKey: bonusKeys.fileAttachments.byEmployee(employeeId),
    queryFn: () => bonusApi.getFileAttachmentsByEmployee(employeeId),
    enabled: !!employeeId,
  });
};

// Hook for fetching file attachments by quarter
export const useFileAttachmentsByQuarter = (quarter: string) => {
  return useQuery<FileAttachment[]>({
    queryKey: bonusKeys.fileAttachments.byQuarter(quarter),
    queryFn: () => bonusApi.getFileAttachmentsByQuarter(quarter),
    enabled: !!quarter,
  });
};

// Hook for downloading a file attachment
export const useDownloadFileAttachment = () => {
  return useMutation({
    mutationFn: (fileId: number) => bonusApi.downloadAttachmentFile(fileId),
  });
};

// Hook for deleting a file attachment
export const useDeleteFileAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (fileId: number) => bonusApi.deleteFileAttachment(fileId),
    onSuccess: () => {
      // Invalidate all file attachment queries
      queryClient.invalidateQueries({
        queryKey: bonusKeys.fileAttachments.all,
      });
    },
  });
};
